/*
Persistent MapLibre host.

The router wipes `#app` (`inRoot.innerHTML = ''`) on every navigation, so a
map mounted inside the screen template is destroyed when you leave it.
Re-creating the MapLibre instance on every return to `#/map` was the root
cause of a capture-killing reload: each re-create spins up a fresh WebGL
context and re-ingests the 33 MB geojson (cluster index rebuild), spiking
native/GPU memory enough that the OS evicted the backgrounded WebContent
during the next camera excursion (reload → photo lost). Measured: heap jumped
+69 MB across one map re-mount, then the next capture reloaded.

The fix — mandated by CLAUDE.md ("MapLibre instanciée une fois … ne pas la
recréer entre routes") — keeps ONE instance alive for the tab's lifetime.
Rather than fight z-index/pointer-events with a body-level overlay, we keep
the map container in the screen's normal flow (same stacking + touch
behaviour as the old `#map-canvas`) and simply MOVE the DOM node across the
router's innerHTML wipes: detach before the wipe, re-attach on the next map
mount. Re-parenting a canvas preserves its WebGL context; a resize() on
re-attach recomputes the layout. The MapLibre controller is never destroyed.
*/

import type { MapController } from './MapView'

let fHost: HTMLElement | null = null          // the persistent map container, created once
let fController: MapController | null = null   // the single MapLibre controller, set once creation resolves
let fControllerPromise: Promise<MapController> | null = null  // memoized CREATION promise — see attachMap

/*
Attach the persistent map into `inParent` (the map screen's `.screen`) as an
in-flow child, creating the container + MapLibre instance on first call and
reusing them forever after. Returns the controller. Async because MapLibre
(`./MapView`, ~600 KB) is dynamically imported so it stays out of the eager
bundle — this module is also imported by the router for `detachMap`, which
must NOT pull maplibre-gl into the initial chunk (hence the type-only import
above + the dynamic import here).
*/
export async function attachMap(inParent: HTMLElement): Promise<MapController>
{
	if (!fHost)
	{
		const vEl = document.createElement('div')
		vEl.id = 'map-host'
		vEl.className = 'map-canvas'  // inherit the flex:1 sizing the in-flow canvas used to have
		fHost = vEl
	}
	// In-flow, first child: positioned FAB/sheet/splash overlays paint above
	// it exactly as they did over the old #map-canvas; empty gaps pass touches
	// through to the map. No z-index / pointer-events changes needed.
	inParent.insertBefore(fHost, inParent.firstChild)

	// Memoize the creation PROMISE, not the resolved controller: with a plain
	// `if (!fController)` check, two concurrent mounts both pass the null
	// check while the dynamic import is in flight and each creates its own
	// MapLibre instance — two stacked canvases in the same host, where the
	// visible one may be the one still loading its data. Memoizing the
	// promise makes the second caller await the first creation instead.
	if (!fControllerPromise)
	{
		const vHost = fHost
		fControllerPromise = import('./MapView').then(({ mountMap }) => {
			// lazy import — keeps maplibre-gl out of the initial bundle; the
			// host is already in the DOM + sized, so the map inits at the right size
			fController = mountMap(vHost)
			return fController
		})
		return fControllerPromise
	}
	const vController = await fControllerPromise
	vController.resize()  // re-attached into a freshly-built screen — recompute the canvas size
	return vController
}

/*
Detach the host from its current parent, keeping the element + MapLibre
instance alive in module state. Called by the router right before every
`innerHTML = ''` so the wipe can't destroy the map. No-op before the map has
ever been created.
*/
export function detachMap(): void
{
	if (fHost?.parentNode) fHost.parentNode.removeChild(fHost)
}

/* True once the persistent map has been created — lets the map screen know
   whether this is the cold first mount (show splash + await pins) or a cheap
   return (skip the splash, the map is already rendered). */
export function isMapCreated(): boolean
{
	return fController !== null
}
